/**
 * A workspace's two session rules (canon 11 §2.2 and G01): what happens when a
 * session's items change after someone has voted, and what deleting a cycle
 * with open sessions does to them.
 *
 * The database applies both, whoever makes the change (migration 1063): a
 * trigger on the session row for item changes, one on the cycle row for a
 * delete. This file names the options, sets them (migration 1052), and says
 * ahead of a delete what will happen.
 */
import { getTypedSupabaseClient } from './typedSupabaseClient';

export type ItemChangeRule = 'lock' | 'invalidate' | 'remediate' | 'flag';
export type CycleDeleteRule = 'block' | 'detach' | 'cascade';

export const ITEM_CHANGE_OPTIONS: ReadonlyArray<{ value: ItemChangeRule; label: string; help: string }> = [
  { value: 'lock', label: 'Locked', help: 'Once someone has voted, the session\'s items cannot change. Start a new session to change them.' },
  { value: 'invalidate', label: 'Everyone votes again', help: 'The items may change; every vote is cleared and each voter is told to vote again.' },
  {
    value: 'remediate',
    label: 'Vote only on what is new',
    help: 'The items may change; voters keep their answers and are asked about the new items only. In a ranking, dots or budget session a rank only means something against every other item, so a voter whose session gains an item votes again.',
  },
  { value: 'flag', label: 'Keep the votes and note the change', help: 'The items may change; every vote is kept, and the results carry a permanent note of what changed, when, and by whom.' },
];

export const CYCLE_DELETE_OPTIONS: ReadonlyArray<{ value: CycleDeleteRule; label: string; help: string }> = [
  { value: 'block', label: 'Not allowed', help: 'A cycle with open sessions cannot be deleted. Close or archive the sessions first.' },
  { value: 'detach', label: 'Keep the sessions on their own', help: 'The cycle is deleted; its open sessions and their votes stay, no longer in a cycle.' },
  { value: 'cascade', label: 'Delete the sessions too', help: 'The cycle and its open sessions are deleted. Published sessions are kept, no longer in a cycle.' },
];

const ITEM_CHANGE_VALUES: readonly string[] = ITEM_CHANGE_OPTIONS.map((o) => o.value);
const CYCLE_DELETE_VALUES: readonly string[] = CYCLE_DELETE_OPTIONS.map((o) => o.value);

/** The rule in effect for a stored value; nothing stored is the default, lock. */
export function itemChangeRule(stored: string | null | undefined): ItemChangeRule {
  return stored && ITEM_CHANGE_VALUES.includes(stored) ? (stored as ItemChangeRule) : 'lock';
}

/** The rule in effect for a stored value; nothing stored is the default, block. */
export function cycleDeleteRule(stored: string | null | undefined): CycleDeleteRule {
  return stored && CYCLE_DELETE_VALUES.includes(stored) ? (stored as CycleDeleteRule) : 'block';
}

export async function setWorkspaceSessionRules(
  workspaceId: string,
  rules: { itemChange?: ItemChangeRule; cycleDelete?: CycleDeleteRule },
): Promise<{ ok: true; itemChange: ItemChangeRule; cycleDelete: CycleDeleteRule } | { ok: false; error: string }> {
  const { data, error } = await getTypedSupabaseClient().rpc('set_workspace_session_rules', {
    p_workspace_id: workspaceId,
    p_item_change: rules.itemChange,
    p_cycle_delete: rules.cycleDelete,
  });
  if (error) return { ok: false, error: error.message };
  const saved = (data ?? {}) as { item_change?: string; cycle_delete?: string };
  return { ok: true, itemChange: itemChangeRule(saved.item_change), cycleDelete: cycleDeleteRule(saved.cycle_delete) };
}

/**
 * What deleting this cycle will do to its open (unpublished) sessions under the
 * workspace's rule — said before the person confirms (canon 11 G01). `blocked`
 * means the database will refuse; `line` is the sentence for the confirmation.
 */
export async function cycleDeleteConsequence(
  cycleId: string,
  storedRule: string | null | undefined,
): Promise<{ blocked: boolean; openSessions: number; line: string }> {
  const rule = cycleDeleteRule(storedRule);
  const { data } = await getTypedSupabaseClient()
    .from('prioritization_sessions')
    .select('name')
    .eq('cycle_id', cycleId)
    .neq('state', 'PUBLISHED');
  const names = (data ?? []).map((s) => s.name as string);
  const n = names.length;
  if (n === 0) return { blocked: false, openSessions: 0, line: '' };
  const listed = names.slice(0, 5).join(', ') + (n > 5 ? ' and others' : '');
  const sessions = n === 1 ? '1 open session' : `${n} open sessions`;
  if (rule === 'block') {
    return {
      blocked: true,
      openSessions: n,
      line: `This cycle has ${sessions} (${listed}), so it cannot be deleted. Close or archive ${n === 1 ? 'it' : 'them'} first; a workspace admin can change this rule on Settings → Workflows.`,
    };
  }
  if (rule === 'cascade') {
    return { blocked: false, openSessions: n, line: `Its ${sessions} (${listed}) will be deleted with it. Published sessions are kept.` };
  }
  return { blocked: false, openSessions: n, line: `Its ${sessions} (${listed}) will be kept, no longer in a cycle.` };
}
